import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import _ from "lodash";

import { getNextQuestionAndIndex } from '../../../utils/surveyNavigation';
import { Typography, Box, Rating } from '@mui/material';
import { styled } from '@mui/material/styles';

import SentimentVeryDissatisfiedIcon from '@mui/icons-material/SentimentVeryDissatisfied';
import SentimentDissatisfiedIcon from '@mui/icons-material/SentimentDissatisfied';
import SentimentSatisfiedIcon from '@mui/icons-material/SentimentSatisfied';
import SentimentSatisfiedAltIcon from '@mui/icons-material/SentimentSatisfiedAltOutlined';
import SentimentVerySatisfiedIcon from '@mui/icons-material/SentimentVerySatisfied';

const RatingComponent = ({question, handleNext, handleAnswer, survey_answers, conditional, conditionalConfig, survey}) => {

    const [value, setValue] = useState(0);
    const [activeStep, setActiveStep] = useState(false)
    const [answer_exists, setAnswerExists] = useState(false)

    // Use conditionalConfig if provided, otherwise use question
    const activeQuestion = conditional && conditionalConfig ? {
        ...question,
        number: conditionalConfig.number,
        question: conditionalConfig.response_question,
        type_config: conditionalConfig.type_config || question.type_config || { format: 'numerical', rate_amount: 5 }
    } : question;

    useEffect(() => {
        if(!activeStep) {
            setActiveStep(true)
        }
        if(survey_answers.length !== 0) {
            let answer
            if(conditional && conditionalConfig) {
                answer = _.find(survey_answers, { 'question_number': conditionalConfig.number });
            } else {
                answer = _.find(survey_answers, { 'question_number': question.number });
            }
            if(answer) {
                setAnswerExists(true)
                setValue(parseInt(answer.user_response));
            }
        }
    }, []);

    useEffect(() => {
        let timeoutId;
        if (value !== 0 && !answer_exists) {
            timeoutId = setTimeout(async () => {
                const answer = {
                    question: activeQuestion.question,
                    question_number: activeQuestion.number,
                    question_type: activeQuestion.type || (conditional && conditionalConfig ? conditionalConfig.type : question.type),
                    user_response: value,
                };
                await handleAnswer(answer);
                const { nextQuestion, nextQuestionIndex } = getNextQuestionAndIndex({
                    question,
                    activeQuestion,
                    conditionalConfig,
                    conditional,
                    survey,
                });
                handleNext(nextQuestion, nextQuestionIndex);
            }, 1000);
        }
        return () => {
            if (timeoutId) clearTimeout(timeoutId);
        };
    }, [value, answer_exists, question, conditionalConfig, conditional, activeQuestion, survey, handleAnswer, handleNext]);

    const handleChange = (event) => {
        setValue(parseInt(event.target.value));
        setAnswerExists(false)
    };

    const StyledRating = styled(Rating)(({ theme }) => ({
        '& .MuiRating-iconEmpty .MuiSvgIcon-root': {
          color: theme.palette.action.disabled,
        },
    }));
      
    const customIcons = {
    1: {
        icon: <SentimentVeryDissatisfiedIcon color="error" />,
        label: 'Very Dissatisfied',
    },
    2: {
        icon: <SentimentDissatisfiedIcon color="error" />,
        label: 'Dissatisfied',
    },
    3: {
        icon: <SentimentSatisfiedIcon color="warning" />,
        label: 'Neutral',
    },
    4: {
        icon: <SentimentSatisfiedAltIcon color="success" />,
        label: 'Satisfied',
    },
    5: {
        icon: <SentimentVerySatisfiedIcon color="success" />,
        label: 'Very Satisfied',
    },
    };
      
    function IconContainer(props) {
        const { value, ...other } = props;
        return <span {...other}>{customIcons[value].icon}</span>;
    }
    
    IconContainer.propTypes = {
        value: PropTypes.number.isRequired,
    };

    // Default to numerical if type_config is missing
    const typeConfig = activeQuestion.type_config || { format: 'numerical', rate_amount: 5 };
    
    if(typeConfig.format === 'numerical') {
        return (
            <Box className={activeStep ? 'active' : 'inactive' }>
                    <Typography variant="h1" sx={{fontFamily: 'Open Sans', fontWeight: 400, color: '#002846', textAlign: 'left', pb: 4}}>
                    <Typography variant="body1" component={'span'} sx={{fontFamily: 'VAG Rounded', color: '#002846', textAlign: 'left'}}>{activeQuestion.number}. </Typography>
                        {activeQuestion.question}
                    </Typography>
                    <Rating
                        className={'numerical_rating'}
                        sx={{color: 'primary.main', borderColor: 'primary.main' }}
                        max={typeConfig.rate_amount || 5}
                        name="numerical-rating"
                        value={value}
                        size="large"
                        onChange={handleChange}
                    />
            </Box>
        )
    } else if (typeConfig.format === 'satisfaction') {
        return (
            <Box className={activeStep ? 'active' : 'inactive' }>
                <Typography variant="h1" sx={{fontFamily: 'Open Sans', fontWeight: 400, color: '#002846', textAlign: 'left', pb: 4}}>
                <Typography variant="body1" component={'span'} sx={{fontFamily: 'VAG Rounded', color: '#002846', textAlign: 'left'}}>{activeQuestion.number}. </Typography>
                    {activeQuestion.question}
                </Typography>
                <StyledRating
                    name="satisfaction-rating"
                    value={value}
                    onChange={handleChange}
                    IconContainerComponent={IconContainer}
                    size="large"
                    getLabelText={(value) => customIcons[value].label}
                    highlightSelectedOnly
                />
            </Box>
        );
    }
    if (process.env.NODE_ENV !== 'production') {
        console.warn('RatingComponent: unknown format, defaulting to numerical', typeConfig.format);
    }
    return (
        <Box className={activeStep ? 'active' : 'inactive'}>
            <Typography variant="h1" sx={{ fontFamily: 'Open Sans', fontWeight: 400, color: '#002846', textAlign: 'left', pb: 4 }}>
                <Typography variant="body1" component="span" sx={{ fontFamily: 'VAG Rounded', color: '#002846', textAlign: 'left' }}>{activeQuestion.number}. </Typography>
                {activeQuestion.question}
            </Typography>
            <Rating
                className="numerical_rating"
                sx={{ color: 'primary.main', borderColor: 'primary.main' }}
                max={typeConfig.rate_amount || 5}
                name="numerical-rating-fallback"
                value={value}
                size="large"
                onChange={handleChange}
            />
        </Box>
    );
}
export default RatingComponent;